//librerías
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useNavigate } from "react-router-dom";

//context
import { AuthContext } from "../../../../context/AuthContext";

//componentes
import { Button, Container, Spinner } from "react-bootstrap";
import {
  faPen,
  faSquarePlus,
  faTasks,
  faToggleOff,
  faToggleOn,
} from "@fortawesome/free-solid-svg-icons";

//interfaces
import { TipoServicioInterface } from "../../../../interfaces/TipoServicioInterface";

//hooks
import useFetch from "../../../../hooks/useFetch";
import { ColumnDef } from "@tanstack/react-table";
import CustomTable from "../../../Formularios/CustomTable/CustomTable";
import ForbiddenMessage from "../../../Mensajes/ForbiddenMessage";
import { toast } from "react-toastify";

//estilos
const classes = require("./TipoServicioTable.module.css").default;

const TipoServicioTable = () => {
  const authContext = useContext(AuthContext);
  //variables de estado
  const [tipoServicio, setTipoServicio] = useState<TipoServicioInterface[]>();
  const { sendRequest, error, setError } = useFetch();
  const navigate = useNavigate();

  //Función que trata los tipoServicio traídos de la BD.
  const transformData = useCallback((variable: any) => {
    setTipoServicio(variable.data);
  }, []);

  //Función que llama todos los tipoServicio paginados
  useEffect(() => {
    sendRequest(
      {
        url: "/tipo_servicio",
      },
      transformData
    );
  }, [sendRequest, transformData]);

  //Función que desactiva a un tipoServicio.
  const changeTipoServicioStatus = useCallback(
    (idTipoServicio: number) => {
      if (
        window.confirm(
          "¿Seguro que desea cambiar el estatus del cliente? No podrá agregarle servicios"
        )
      ) {
        sendRequest(
          {
            url: `/tipo_servicio/${idTipoServicio}`,
            method: "DELETE",
          },
          (variable: any) => {
            setTipoServicio([]);
            toast.success("El tipo de servicio ha cambiado de estatus.", {
              position: "top-left",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              theme: "light",
            });
            sendRequest(
              {
                url: "/tipo_servicio",
              },
              transformData
            );
          }
        );
      }
    },
    [sendRequest, transformData]
  );

  const columns = useMemo<ColumnDef<TipoServicioInterface>[]>(
    () => [
      {
        header: "#",
        accessorKey: "id",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
        enableColumnFilter: false,
      },
      {
        header: "Nombre",
        accessorFn: (row) => row.nombre,
        id: "Nombre",
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        header: "Estatus",
        cell: (info) => info.getValue(),
        accessorFn: (row) => (row.estatus ? "Activo" : "Inactivo"),
      },
      {
        header: "Acciones",
        cell: (info) => (
          <div className="btn-group" role="group" aria-label="Basic example">
            <Button
              title="Editar tipo de servicio"
              style={{ backgroundColor: "#5DADE2", borderColor: "white" }}
              onClick={() =>
                navigate(
                  `/main/catalogos/tipoServicio/edit/${info.row.original.id}`
                )
              }
              disabled={!authContext.tokenCan("TipoServicio:update")}
            >
              <FontAwesomeIcon icon={faPen as IconProp} />
            </Button>
            {/** Botón para asignar pruebas*/}
            <Button
              title="Asignar pruebas"
              style={{ backgroundColor: "#2874A6", borderColor: "white" }}
              onClick={() =>
                navigate(
                  `/main/catalogos/tipoServicio/asignarPrueba/${info.row.original.id}`
                )
              }
              disabled={!authContext.tokenCan("TipoServicio:assignPrueba")}
            >
              <FontAwesomeIcon icon={faTasks as IconProp} />
            </Button>
            {/** Botón para ver eliminar*/}
            <Button
              title={
                info.row.original.estatus
                  ? "Deshabilitar tipo de servicio"
                  : "Habilitar tipo de servicio"
              }
              style={{ backgroundColor: "#0d5475", borderColor: "white" }}
              onClick={() => changeTipoServicioStatus(info.row.original.id)}
            >
              <FontAwesomeIcon
                icon={(info.row.original.estatus ? faToggleOn : faToggleOff) as IconProp}
              />
            </Button>
          </div>
        ),
        footer: (props) => props.column.id,
      },
    ],
    [changeTipoServicioStatus, navigate, authContext]
  );

  //Mientras se carga el contenido se muestra un spinner
  if (!tipoServicio) {
    return (
      <div className={classes.spinnerContainer}>
        <Spinner animation="grow" variant="info" />
      </div>
    );
  }

  return (
    <Container>
      <h2>Lista de tipos de servicio</h2>
      {/** Botón para agregar tipo de servicio */}
      {authContext.tokenCan("TipoServicio:store") && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "10px",
          }}
        >
          <Button
            title="Agregar un nuevo tipo de servicio"
            style={{ backgroundColor: "#21618C", borderColor: "white" }}
            onClick={() => navigate(`/main/catalogos/tipoServicio/crear`)}
          >
            <FontAwesomeIcon icon={faSquarePlus as IconProp} /> Nuevo tipo de servicio
          </Button>
        </div>
      )}

      {/** Alerta de permisos */}
      {error === 403 && (
        <ForbiddenMessage onCloseHandler={() => setError(null)} />
      )}
      <CustomTable
        {...{
          data: tipoServicio,
          columns,
        }}
      />
    </Container>
  );
};

export default TipoServicioTable;
